// handles document export and history processing for various sections
import { mostRecentTherapyHistory, processHistorySection, processHistoryData } from "./historyProcessing";

// Helper to check if a value is empty (null, undefined, or whitespace-only)
const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
};

/**
 * Normalize HTML content for export to match Quill editor's paragraph structure.
 * 
 * AI-generated content often stores text as:
 *   - A single <p> with <br> tags for line breaks
 *   - Plain text with <br> tags (no <p> at all)
 *   - Plain text with no HTML tags
 * 
 * Quill internally converts all of these to separate <p> paragraphs when rendering,
 * so users see consistent paragraph spacing in the editor. This function applies the
 * same normalization so the export matches what users see.
 */
const normalizeExportParagraphs = (html) => {
  if (!html || typeof html !== 'string' || html.trim() === '') return html;

  // If content already has multiple <p> tags, it's properly structured (e.g., Quill-edited)
  const pTagCount = (html.match(/<p[\s>]/gi) || []).length;
  if (pTagCount > 1) {
    return html;
  }

  // If content has no <br> tags, just wrap in <p> if needed
  if (!/<br\s*\/?>/i.test(html)) {
    if (pTagCount === 0 && !/<(h[1-6]|div|ul|ol|table|blockquote)\b/i.test(html)) {
      return `<p>${html}</p>`;
    }
    return html;
  }

  // Extract content from single <p> wrapper if present
  let content = html;
  const singlePMatch = html.match(/^<p>([\s\S]*)<\/p>$/);
  if (singlePMatch) {
    content = singlePMatch[1];
  }

  // Split on <br> variants and create separate <p> tags for each non-empty line
  const lines = content.split(/<br\s*\/?>/gi)
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length <= 1) {
    const cleaned = lines[0] || content.replace(/<br\s*\/?>/gi, '').trim();
    return cleaned ? `<p>${cleaned}</p>` : '';
  }

  return lines.map(line => {
    // Don't wrap existing block-level elements in <p>
    if (/^<(h[1-6]|div|ul|ol|table|blockquote)\b/i.test(line)) {
      return line;
    }
    return `<p>${line}</p>`;
  }).join('');
};

export const capitalizeAndFormat = (str) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export function formatListItem(key, value) {
  return (
    <li>
      <strong>{capitalizeAndFormat(key)}:</strong> {value}
    </li>
  );
}

export const generateSectionExport = (section, documentData, dataHistory) => {

const excludedInSettings = section.dataField && documentData?.settings?.[section.dataField]?.excludeFromExport;

if (excludedInSettings ) {
    return {}; }

  switch (section.type) {
    case "StartEndTime":
      const startTimeHtml = documentData.startTime ? `<p style="margin: 0; padding: 0;"><strong>Start Time:</strong> ${documentData.startTime}</p>` : "";
      const endTimeHtml = documentData.endTime ? `<p style="margin: 0; padding: 0;"><strong>End Time:</strong> ${documentData.endTime}</p>` : "";
      const dataHtml = startTimeHtml && endTimeHtml ? `<div class="export-section">${startTimeHtml}${endTimeHtml}</div>` : `${startTimeHtml}${endTimeHtml}`;
      return {
        startEndTime: {
          data: dataHtml,
        },
      };

    case "TherapyTiming":
      const mostRecent = dataHistory ? mostRecentTherapyHistory(dataHistory) : {};

      const docTherapyType = documentData?.therapyType;
      const docTherapyMinutes = documentData?.therapyMinutes;

      const therapyTypeHtml = docTherapyType
        ? `<p style="margin: 0; padding: 0;"><strong>Therapy Type:</strong> ${docTherapyType}</p>`
        : mostRecent?.therapyType
        ? `<p style="margin: 0; padding: 0;"><strong>Therapy Type:</strong> ${mostRecent.therapyType}</p>`
        : "";
      const therapyMinutesHtml = docTherapyMinutes ? `<p style="margin: 0; padding: 0;"><strong>Therapy Minutes:</strong> ${docTherapyMinutes}</p>` : "";
      const therapyDataHtml = therapyTypeHtml || therapyMinutesHtml ? `<div class="export-section-short">${therapyMinutesHtml}${therapyTypeHtml}</div>` : "";
      return {
        therapyTiming: {
          data: therapyDataHtml,
        },
      };

    case "EditableTextSection":
      let textContent = documentData[section.dataField]?.summary || "";
      // Clean up non-breaking spaces that the editor may have inserted
      // These cause line breaks at hyphens since &nbsp; prevents normal word wrapping
      textContent = textContent.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');

      // hideEmpty and hideAllEmpty: return empty object if text content is empty
      if ((section.exportType === "hideEmpty" || section.exportType === "hideAllEmpty") && isEmpty(textContent)) {
        return {};
      }

      // Normalize paragraph structure to match Quill editor rendering
      const normalizedContent = normalizeExportParagraphs(textContent);

      return {
        [section.dataField]: {
          data: normalizedContent,
          title: section.title,
        },
      };

    case "EditableTableSection": // multi-column sections
      const filteredHistory =
        Array.isArray(dataHistory) && dataHistory.length > 0
          ? dataHistory.map((record) => ({
              ...record[section.dataField],
              modifiedAt: record.modifiedAt,
            }))
          : [];

      const getKey = (item) => item.id || item[section.primaryField];
      const processedHistory = processHistoryData(filteredHistory, getKey);

      const tableData = processHistorySection(section, documentData[section.dataField], processedHistory);

      if (!tableData || tableData.length === 0) {
        return {};
      }

      let formattedTableData = "<ul>";
      let currentItems = "";
      let pastItems = "";

      tableData.forEach((item) => {
        if (item.isDeleted || item.isHidden) {
          return;
        }

        if (item.hasOwnProperty("patient_use") && item.patient_use === false) {
          return null;
        }

        if (section.exportType === "currentOnly" && !item.current) {
          return;
        }

        // hideEmpty and splitCurrentPast: skip items where secondaryField (default: summary) is empty
        const secondaryField = section.secondaryField || "summary";
        if ((section.exportType === "hideEmpty" || section.exportType === "splitCurrentPast") && isEmpty(item[secondaryField])) {
          return;
        }

        // hideAllEmpty: skip items where both primaryField AND secondaryField are empty
        if (section.exportType === "hideAllEmpty" && isEmpty(item[section.primaryField]) && isEmpty(item[secondaryField])) {
          return;
        }

        if (item[section.primaryField] === null || item[section.primaryField] === undefined || item[section.primaryField] === "") {
          return;
        }

        let listItem = "<li>";
        let firstColumn = false;
        Object.keys(section.fields).forEach((fieldKey, index, array) => {
          const field = section.fields[fieldKey];
          if (item[fieldKey] && field.type !== "check") {
            if (!firstColumn) {
              listItem += `<strong>${capitalizeAndFormat(item[fieldKey])}:</strong> `;
              firstColumn = true;
            } else {
              listItem += `${item[fieldKey]}`;
              if (index < array.length - 1) {
                listItem += ", ";
              }
            }
          }
        });
        listItem += "</li>";

        if (section.exportType === "splitCurrentPast") {
          if (item.current) {
            currentItems += listItem;
          } else {
            pastItems += listItem;
          }
        } else {
          formattedTableData += listItem;
        }
      });

      if (section.exportType === "splitCurrentPast") {
        // Only show Current header if there are current items
        if (currentItems) {
          formattedTableData += "<h4>Current:</h4><br/>";
          formattedTableData += currentItems;
        }
        // Only show Past header if there are past items
        if (pastItems) {
          if (currentItems) {
            formattedTableData += "<br/>";
          }
          formattedTableData += "<h4>Past:</h4><br/>";
          formattedTableData += pastItems;
        }
        // If both are empty, return empty object
        if (!currentItems && !pastItems) {
          return {};
        }
      }

      formattedTableData += "</ul>";

      return {
        [section.dataField]: {
          data: formattedTableData,
          title: section.title,
        },
      };

    case "Confirmation":
      const confirmationMessage = documentData[section.dataField]?.exportMessage || "";
      return {
        [section.dataField]: {
          data: confirmationMessage,
          title: "",
        },
      };

    default:
      return null;
  }
};
